import {AxiosResponse} from 'axios';
import dayjs from '../../../../services/customDayJs'
import {put, select} from 'redux-saga/effects';
import {ActionType} from 'typesafe-actions';
import {actions} from '../../..';
import {Account, Customer, Products} from '../../../../services/endpoints';
import JSONFormData from '../../../../utils/JSONFormData';
import {showToast} from '../../../../utils/showToast';
import i18n from '../../../../services/i18n';
import {EditExtensionForm} from '../../../../views/Extensions/ExtensionDetails/utils';
import {api} from '../../../services/axios';
import {
    AddonsValidationResults,
    CustomerAgreementCondition,
    getEmptyAddonsValidationResults,
} from '../../../types/CustomerAgreementCondition';
import {YesNo} from '../../../types/CallScreening';
import {ExtensionType} from '../../../types/Extension';
import {ReduxState} from '../../../types';
import {getAccountInfo} from '../extensions/saga';
import { RealmType } from '../../../types/CustomerInfo';
import { ProductInfo } from '../../../actions/accountDashboard/payloads';
import { extensionsApi } from '../../../services/extensions';

function* getAllowedAddonsIfSupportedByBackend(
    session_id: string,
    csrf_token: string,
    i_account: number,
) {
    try {
        const response: AxiosResponse<{
            allowed_addons?: ProductInfo[];
        }> = yield extensionsApi.getAllowedAddons(session_id, csrf_token, { i_account });

        const allowedAddons = response.data?.allowed_addons;

        return Array.isArray(allowedAddons) ? allowedAddons : undefined;
    } catch {
        return undefined;
    }
}

function markUnassignedAddonsMissingFromAllowedList(
    agreementAddons: CustomerAgreementCondition[],
    allowedAddons: ProductInfo[],
) {
    agreementAddons.forEach((addon) => {
        const isAssigned = addon.managed_by_user !== undefined;
        if (isAssigned) {
            return;
        }
        const allowed = allowedAddons.find((a) => a.i_product == addon.i_product);
        if (allowed) {
            addon.managed_by_user = allowed.managed_by_user;
        } else {
            addon.notInAllowedAddons = true;
        }
    });
}

export function* getExtensionProducts(i_account?: number) {
    try {
        const { session_id, csrf_token } = yield select((state: ReduxState) => state.auth);
        const body = new JSONFormData(session_id, csrf_token);

        const realm: RealmType = yield select(
            (state: ReduxState) => state?.generic?.sessionData?.realm ?? RealmType.Retail
        );

        if(realm == RealmType.Account)
        {
            const {session_id, csrf_token} = yield select((state: ReduxState) => state.auth);
            const body = new JSONFormData(session_id, csrf_token);
        
            body.setParams({
                i_account: i_account
            });
        
            const response: AxiosResponse<{
                product_info: ProductInfo
            }> = yield api.post(Account.GetAccountProductInfo, body);

            const accountProductInfo = response.data.product_info;

            const responseAllowedAddOns: AxiosResponse<{
                allowed_addons: ProductInfo[]
            }> = yield  extensionsApi.getAllowedAddons(session_id, csrf_token);

            yield put(
                actions.getExtensionProducts.success({
                    mainProducts: [{
                        is_addon: 0,
                        max_offered_quantity: 0,
                        product_name: accountProductInfo.end_user_name?.length ? accountProductInfo.end_user_name : accountProductInfo.name,
                        used_quantity: 0,
                        i_product: accountProductInfo.i_product
                    }],
                    addonsProducts: responseAllowedAddOns.data.allowed_addons.map(e => {
                        return {
                            i_product: e.i_product,
                            i_product_group: e.i_product_group,
                            is_addon: 1,
                            max_offered_quantity: 0,
                            product_name: e.end_user_name?.length ? e.end_user_name : e.name,
                            used_quantity: 0,
                            managed_by_user: e.managed_by_user,
                        } as CustomerAgreementCondition;
                    })
                }),
            );
        }
        else
        {     
            body.setParams({
                with_usage: '1',
            });

            const res: AxiosResponse<{
                agreement_condition_list: CustomerAgreementCondition[];
            }> = yield api.post(Customer.GetCustomerAgreementInfo, body);

            let extraProduct: CustomerAgreementCondition | undefined;
            const extraAddons: CustomerAgreementCondition[] = [];

            const addons = res.data.agreement_condition_list.filter(
                (v) => v.is_addon === 1,
            );

            if (i_account) {
                const accountInfoRes: AxiosResponse<{
                    account_info: ExtensionType;
                }> = yield getAccountInfo(i_account);

                const { account_info } = accountInfoRes.data;
                if (account_info.product_name && account_info.i_product) {
                    extraProduct = {
                        product_name: account_info.product_name,
                        i_product: account_info.i_product,
                        is_addon: 1,
                        max_offered_quantity: 0,
                        used_quantity: 1,
                    };
                }

                if (account_info.assigned_addons) {
                    const temp: CustomerAgreementCondition[] = account_info.assigned_addons.map(
                        (a) => {
                            return {
                                i_product: a.i_product,
                                is_addon: 1,
                                max_offered_quantity: 0,
                                product_name: a.name || a.end_user_name,
                                used_quantity: 1,
                                managed_by_user: a.managed_by_user,
                        };
                        },
                    );

                    temp.forEach((item) => {
                        const existing = addons.find((a) => a.i_product == item.i_product);
                        if (!existing) {
                            extraAddons.push(item);
                        } else if (item.managed_by_user !== undefined) {
                            existing.managed_by_user = item.managed_by_user;
                        }
                    });
                }

                const allowedAddons: ProductInfo[] | undefined = yield getAllowedAddonsIfSupportedByBackend(
                    session_id,
                    csrf_token,
                    i_account,
                );

                // An empty list (e.g. main product outside the agreement) tells nothing
                // about the add-ons, so nothing is inferred from it
                if (allowedAddons?.length) {
                    markUnassignedAddonsMissingFromAllowedList(addons, allowedAddons);
                }
            }

            const products = res.data.agreement_condition_list.filter(
                (v) => v.is_addon === 0,
            );

            const mainProducts =
                extraProduct &&
                !products.find((p) => p.i_product == extraProduct?.i_product)
                    ? [...products, extraProduct]
                    : products;

            const addonsProducts = extraAddons.length
                ? [...addons, ...extraAddons]
                : addons;

            yield put(
                actions.getExtensionProducts.success({
                    mainProducts: mainProducts,
                    addonsProducts: addonsProducts,
                }),
            );
        }
    } catch (err) {
        yield put(
            actions.getExtensionProducts.success({
                mainProducts: [],
                addonsProducts: [],
            }),
        );
    }
}

export function* editExtensionPlan(
    data: Partial<EditExtensionForm>,
    action: ActionType<typeof actions.editExtension.request>,
) {
    try {
        const { session_id, csrf_token } = yield select((state) => state.auth);

        const body = new JSONFormData(session_id, csrf_token);

        if (data.selectedMainProduct !== undefined || data.selectedAddonsProducts) {
            const selectedMainProduct =
                data.selectedMainProduct ||
                action.payload.initialValues.selectedMainProduct;
            const selectedAddonsProduct =
                data.selectedAddonsProducts ||
                action.payload.initialValues.selectedAddonsProducts;

            body.setParams({
                account_info: {
                    i_account: action.payload.accountId,
                    i_product: selectedMainProduct,
                    assigned_addons: selectedAddonsProduct.map((v) => ({
                        i_product: v,
                    })),
                },
            });

            yield api.post(Account.UpdateAccount, body);
        }
    } catch (err: any) {
        yield put(actions.editExtension.failure(err.response?.data));
    }
}

export function* validateAddonsProducts(
    action: ActionType<typeof actions.validateAddonsProducts.request>,
) {
    try {
        const { session_id, csrf_token } = yield select((state) => state.auth);
        const addonsProducts: CustomerAgreementCondition[] | undefined = yield select(
            (state) => state.extensions.products?.addonsProducts,
        );

        if (!addonsProducts) {
            yield put(actions.validateAddonsProducts.failure());
            return;
        }

        const realm: RealmType = yield select(
            (state: ReduxState) => state?.generic?.sessionData?.realm ?? RealmType.Retail
        );

        // In the Account realm the API rejects add-ons not managed by the end user
        // (object_access_not_allowed), so they are left out of the validation
        const addonsToValidate = realm == RealmType.Account
            ? addonsProducts.filter((v) => v.managed_by_user !== YesNo.No)
            : addonsProducts;

        if (!addonsToValidate.length) {
            yield put(actions.validateAddonsProducts.success(getEmptyAddonsValidationResults()));
            return;
        }

        const body = new JSONFormData(session_id, csrf_token);

        const dateNow = dayjs.utc().format('YYYY-MM-DD HH:mm:ss');

        body.setParams({
            i_product: action.payload.mainProductId,
            selected_products: addonsToValidate.map((v) => ({
                i_product: v.i_product,
                effective_from: dateNow,
                effective_to: null,
            })),
        });

        const response: AxiosResponse<{
            addons_combination_validation_result: AddonsValidationResults;
        }> = yield api.post(Products.ValidateAddonsCombination, body);

        yield put(
            actions.validateAddonsProducts.success(
                response.data.addons_combination_validation_result,
            ),
        );
    } catch (err: any) {
        showToast(err.response?.data?.faultstring || i18n.t<string>('errors:common.genericError'));
        yield put(actions.validateAddonsProducts.failure());
    }
}
